import { Component, input, signal, effect } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import {
    TableRowData,
    hasStatus,
    hasReadOnly,
    hasNumberOfDeliveryNotes,
    hasDateReceived,
    hasFineDiscrepancies,
    hasDeliveryNoteBundleKey,
} from '@app/shared/models/table-data-types';
import {
    ColumnConfig,
    ColumnValue,
} from '@app/shared/services/view-configs/column-creator';

export interface TableActionsConfig {
    // Component type determines which UI and actions are available
    type: 'bundle' | 'deliveryNote' | 'notification';

    // Common actions - available for both types
    showView?: boolean;
    showDownload?: boolean;
    showEdit?: boolean;
    showDelete?: boolean;
    showDeletePermanent?: boolean;

    // Bundle-specific actions
    showDeliveryNotesCount?: boolean;
    showWalletInvite?: boolean;
    showAddNotes?: boolean;
    showViewPdf?: boolean;

    // Notification-specific actions
    showGoToProcess?: boolean;

    // Action endpoints - typed based on context
    viewEndpoint?: (
        item: TableRowData,
        parentRow?: TableRowData
    ) => Promise<void> | void;
    downloadEndpoint?: (
        item: TableRowData,
        parentRow?: TableRowData
    ) => Promise<void> | void;
    editEndpoint?: (
        item: TableRowData,
        parentRow?: TableRowData
    ) => Promise<void> | void;

    // Bundle-specific endpoints
    viewPdfEndpoint?: (item: TableRowData) => Promise<void> | void;
    walletInviteEndpoint?: (item: TableRowData) => Promise<void> | void;
    addNotesEndpoint?: (
        item: TableRowData,
        parentRow?: TableRowData
    ) => Promise<void> | void;

    // Notification-specific endpoints
    goToProcessEndpoint?: (item: TableRowData) => Promise<void> | void;

    // Delete endpoint - different signatures for bundle vs delivery note
    deleteEndpoint?: (
        item: TableRowData,
        bundleKey?: string,
        parentRow?: TableRowData
    ) => Promise<void> | void;
    deletePermanentEndpoint?: (item: TableRowData) => Promise<void> | void;

    // Tooltips
    viewTooltip?: string;
    downloadTooltip?: string;
    editTooltip?: string;
    deleteTooltip?: string;
    deletePermanentTooltip?: string;
    viewPdfTooltip?: string;
    walletInviteTooltip?: string;
    addNotesTooltip?: string;
    goToProcessTooltip?: string;

    // Visibility conditions
    isEditVisible?: (item: TableRowData) => boolean;
    isDeleteVisible?: (item: TableRowData) => boolean;
    isDeletePermanentVisible?: (item: TableRowData) => boolean;
    isViewVisible?: (item: TableRowData) => boolean;
    isDownloadVisible?: (item: TableRowData) => boolean;
    isWalletInviteVisible?: (item: TableRowData) => boolean;
    isAddNotesVisible?: (item: TableRowData) => boolean;
    isGoToProcessVisible?: (item: TableRowData) => boolean;

    // Configuration context
    organizationSiteKey?: string;
    bundleKey?: string;
    bundleStatus?: string;
    context?: 'consignor' | 'carrier' | 'consignee';
    deliveryNotesCountClickable?: boolean;
}

@Component({
    selector: 'app-table-actions',
    standalone: true,
    imports: [
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        TranslateModule,
    ],
    template: `
        <div class="tw-flex tw-items-center tw-justify-start">
            <!-- Bundle-specific: Delivery Notes Count Badge with optional Add Notes functionality -->
            @if (
                isBundleType() &&
                (config().showDeliveryNotesCount || config().showAddNotes)
            ) {
                <div
                    class="table-badge"
                    [class.tw-cursor-pointer]="shouldShowAddNotesAction()"
                    [matTooltip]="getDeliveryNotesTooltip() | translate"
                    [matTooltipDisabled]="!shouldShowAddNotesAction()"
                    [attr.data-testid]="rowTestId('add-notes')"
                    (click)="handleAddNotes()">
                    {{ getDeliveryNotesCount() }}
                    @if (shouldShowAddNotesAction()) {
                        <div class="table-badge__delete">
                            <mat-icon
                                class="!tw-mr-0"
                                fontSet="material-symbols-outlined"
                                >add</mat-icon
                            >
                        </div>
                    }
                </div>
            }

            <!-- Download Button -->
            @if (shouldShowDownload()) {
                <div class="place">
                    <button
                        mat-icon-button
                        [attr.aria-label]="getDownloadTooltip() | translate"
                        [matTooltip]="getDownloadTooltip() | translate"
                        [attr.data-testid]="rowTestId('download')"
                        (click)="handleDownload()">
                        <mat-icon fontSet="material-symbols-outlined"
                            >file_download</mat-icon
                        >
                    </button>
                </div>
            }

            <!-- View Button (different behavior for bundle vs delivery note) -->
            @if (shouldShowView()) {
                <div class="place">
                    <button
                        mat-icon-button
                        [attr.aria-label]="getViewTooltip() | translate"
                        [matTooltip]="getViewTooltip() | translate"
                        [attr.data-testid]="rowTestId('view')"
                        (click)="handleView()">
                        <mat-icon fontSet="material-symbols-outlined"
                            >visibility</mat-icon
                        >
                    </button>
                </div>
            }

            <!-- Edit Button -->
            @if (shouldShowEdit()) {
                <div class="place">
                    <button
                        mat-icon-button
                        [attr.aria-label]="getEditTooltip() | translate"
                        [matTooltip]="getEditTooltip() | translate"
                        [attr.data-testid]="rowTestId('edit')"
                        (click)="handleEdit()">
                        <mat-icon fontSet="material-symbols-outlined"
                            >edit</mat-icon
                        >
                    </button>
                </div>
            }

            <!-- Bundle-specific: Wallet Invite (QR Code) Button -->
            @if (isBundleType() && shouldShowWalletInvite()) {
                <div class="place">
                    <button
                        mat-icon-button
                        [attr.aria-label]="
                            config().walletInviteTooltip ||
                                'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.SEND-QR-CODE'
                                | translate
                        "
                        [matTooltip]="
                            config().walletInviteTooltip ||
                                'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.SEND-QR-CODE'
                                | translate
                        "
                        [attr.data-testid]="rowTestId('wallet-invite')"
                        (click)="handleWalletInvite()">
                        <mat-icon fontSet="material-symbols-outlined"
                            >qr_code_scanner</mat-icon
                        >
                    </button>
                </div>
            }

            <!-- Delete Button (different icons for bundle vs delivery note) -->
            @if (shouldShowDelete()) {
                <div class="place">
                    <button
                        mat-icon-button
                        [attr.aria-label]="getDeleteTooltip() | translate"
                        [matTooltip]="getDeleteTooltip() | translate"
                        [attr.data-testid]="rowTestId('delete')"
                        (click)="handleDelete()">
                        <mat-icon fontSet="material-symbols-outlined">{{
                            getDeleteIcon()
                        }}</mat-icon>
                    </button>
                </div>
            }

            <!-- Permanent Delete Button (trash icon) -->
            @if (shouldShowDeletePermanent()) {
                <div class="place">
                    <button
                        mat-icon-button
                        [attr.aria-label]="
                            getDeletePermanentTooltip() | translate
                        "
                        [matTooltip]="getDeletePermanentTooltip() | translate"
                        [attr.data-testid]="rowTestId('delete-permanent')"
                        (click)="handleDeletePermanent()">
                        <mat-icon fontSet="material-symbols-outlined"
                            >delete</mat-icon
                        >
                    </button>
                </div>
            }

            <!-- Delivery Note specific: Add Fine Discrepancy Button -->
            @if (isDeliveryNoteType() && shouldShowAddNotesAction()) {
                <div class="place">
                    <button
                        mat-icon-button
                        aria-label="add-fine-discrepancy"
                        [matTooltip]="
                            config().addNotesTooltip ||
                                'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.ADD-DELIVERY-NOTE-APPENDIX'
                                | translate
                        "
                        [attr.data-testid]="rowTestId('add-notes')"
                        (click)="handleAddNotes()">
                        <mat-icon svgIcon="add-deviations"></mat-icon>
                    </button>
                </div>
            }

            <!-- Bundle-specific: View PDF Button (separate from view for bundles) -->
            @if (isBundleType() && config().showViewPdf) {
                <div class="place">
                    <button
                        mat-icon-button
                        [attr.aria-label]="
                            config().viewPdfTooltip ||
                                'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.VIEW-FOLDER'
                                | translate
                        "
                        [matTooltip]="
                            config().viewPdfTooltip ||
                                'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.VIEW-FOLDER'
                                | translate
                        "
                        [attr.data-testid]="rowTestId('view-pdf')"
                        (click)="handleViewPdf()">
                        <mat-icon fontSet="material-symbols-outlined"
                            >visibility</mat-icon
                        >
                    </button>
                </div>
            }

            <!-- Notification-specific: Go To Process Button (Zum Vorgang) -->
            @if (isNotificationType() && shouldShowGoToProcess()) {
                <div class="place">
                    <button
                        mat-raised-button
                        color="primary"
                        class="tw-min-w-[80px] tw-px-4 tw-whitespace-nowrap"
                        [matTooltip]="
                            config().goToProcessTooltip ||
                                'OUTGOING-GOODS.NOTIFICATION-VIEW.JUMP_TO_CHECKIN'
                                | translate
                        "
                        [attr.data-testid]="rowTestId('go-to-process')"
                        (click)="handleGoToProcess()">
                        {{
                            'OUTGOING-GOODS.NOTIFICATION-VIEW.JUMP_TO_CHECKIN'
                                | translate
                        }}
                    </button>
                </div>
            }
        </div>
    `,
    styles: [
        `
            .table-badge {
                background-color: #e3f2fd;
                color: #1976d2;
                border-radius: 4px;
                padding: 2px 8px;
                font-size: 12px;
                font-weight: 500;
                margin-right: 8px;
                position: relative;
                display: inline-flex;
                align-items: center;
            }
            .table-badge__delete {
                display: inline-flex;
                align-items: center;
                margin-left: 4px;
            }
            .place {
                margin-left: 4px;
            }
        `,
    ],
})
export class TableActionsComponent {
    item = input<TableRowData>();
    configuration = input<TableActionsConfig>();

    // Additional inputs that the generic table may pass
    row = input<TableRowData>();
    column = input<ColumnConfig>();
    value = input<ColumnValue>();
    index = input<number>();
    parentRow = input<TableRowData>();

    data = signal<TableRowData>({} as TableRowData);
    config = signal<TableActionsConfig>({
        type: 'bundle',
        showView: true,
        showDownload: true,
    });

    constructor() {
        // Preserve the original setter behavior: update the backing signals
        // only when the corresponding input actually receives a value.
        effect(() => {
            const item = this.item();
            if (item !== undefined) {
                this.data.set(item);
            }
        });
        effect(() => {
            const configuration = this.configuration();
            if (configuration !== undefined) {
                this.config.set(configuration);
            }
        });
    }

    // Type checking
    isBundleType(): boolean {
        return this.config().type === 'bundle';
    }

    isDeliveryNoteType(): boolean {
        return this.config().type === 'deliveryNote';
    }

    isNotificationType(): boolean {
        return this.config().type === 'notification';
    }

    // Visibility check methods
    shouldShowView(): boolean {
        const config = this.config();
        if (!config.showView) return false;
        if (config.isViewVisible) {
            return config.isViewVisible(this.data());
        }
        return true;
    }

    shouldShowDownload(): boolean {
        const config = this.config();
        if (!config.showDownload) return false;
        if (config.isDownloadVisible) {
            return config.isDownloadVisible(this.data());
        }

        // Bundle-specific logic: show if there are delivery notes
        if (this.isBundleType()) {
            const item = this.data();

            // Check for numberOfDeliveryNotes property (for folders/bundles)
            if (
                hasNumberOfDeliveryNotes(item) &&
                item.numberOfDeliveryNotes > 0
            ) {
                return true;
            }

            // Check for deliveryNoteKeys array (for tours)
            if (
                item &&
                'deliveryNoteKeys' in item &&
                Array.isArray(item.deliveryNoteKeys) &&
                item.deliveryNoteKeys.length > 0
            ) {
                return true;
            }

            return false;
        }

        return true;
    }

    shouldShowEdit(): boolean {
        const config = this.config();
        if (!config.showEdit) return false;
        if (config.isEditVisible) {
            return config.isEditVisible(this.data());
        }

        // Bundle-specific logic
        if (this.isBundleType()) {
            const item = this.data();
            return (
                hasStatus(item) &&
                item.status === 'open' &&
                (!hasReadOnly(item) || !item.read_only)
            );
        }

        // Delivery note logic
        return config.bundleStatus === 'open';
    }

    shouldShowDelete(): boolean {
        const config = this.config();
        if (!config.showDelete) return false;
        if (config.isDeleteVisible) {
            return config.isDeleteVisible(this.data());
        }

        // Bundle-specific logic
        if (this.isBundleType()) {
            const item = this.data();
            return (
                hasStatus(item) &&
                item.status === 'open' &&
                (!hasReadOnly(item) || !item.read_only)
            );
        }

        // Delivery note logic
        return config.bundleStatus === 'open';
    }

    shouldShowWalletInvite(): boolean {
        const config = this.config();
        if (!config.showWalletInvite) return false;
        if (config.isWalletInviteVisible) {
            return config.isWalletInviteVisible(this.data());
        }

        // Default: show for sent status and not read-only
        const item = this.data();
        return (
            hasStatus(item) &&
            item.status === 'sent' &&
            (!hasReadOnly(item) || !item.read_only)
        );
    }

    shouldShowAddNotesAction(): boolean {
        const config = this.config();
        if (!config.showAddNotes) return false;
        if (config.isAddNotesVisible) {
            return config.isAddNotesVisible(this.data());
        }

        // Bundle-specific logic: not clickable for sent/closed status or read-only
        if (this.isBundleType()) {
            const item = this.data();
            return !(
                (hasStatus(item) &&
                    (item.status === 'sent' || item.status === 'closed')) ||
                (hasReadOnly(item) && item.read_only)
            );
        }

        // Delivery note logic: show add notes for received notes without existing fine discrepancies
        if (this.isDeliveryNoteType()) {
            const item = this.data();
            return (
                hasDateReceived(item) &&
                item.dateReceived > 0 &&
                (!hasFineDiscrepancies(item) || !item.fineDiscrepancies)
            );
        }

        return false;
    }

    shouldShowDeletePermanent(): boolean {
        const config = this.config();
        if (!config.showDeletePermanent) return false;
        if (config.isDeletePermanentVisible) {
            return config.isDeletePermanentVisible(this.data());
        }

        if (this.isDeliveryNoteType()) {
            const item = this.data();
            const hasNoBundle =
                !hasDeliveryNoteBundleKey(item) ||
                !item.deliveryNoteBundleKey.length;
            const isNotReadOnly = !hasReadOnly(item) || !item.read_only;
            return hasNoBundle && isNotReadOnly;
        }

        return false;
    }

    shouldShowGoToProcess(): boolean {
        const config = this.config();
        if (!config.showGoToProcess) return false;
        if (config.isGoToProcessVisible) {
            return config.isGoToProcessVisible(this.data());
        }
        return true;
    }

    // Tooltip methods
    getViewTooltip(): string {
        const config = this.config();
        if (config.viewTooltip) return config.viewTooltip;

        return this.isBundleType()
            ? 'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.VIEW-FOLDER'
            : 'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.VIEW-DELIVERY-NOTE';
    }

    getDownloadTooltip(): string {
        const config = this.config();
        if (config.downloadTooltip) return config.downloadTooltip;

        return this.isBundleType()
            ? 'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.DOWNLOAD-BUNDLE'
            : 'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.DOWNLOAD-DELIVERY-NOTE';
    }

    /**
     * Eindeutiger Testanker je Zeile: Der zugängliche Name der Aktionen ist
     * in jeder Zeile gleich — erst der Zeilenschlüssel macht ihn adressierbar.
     */
    rowTestId(action: string): string {
        const item = this.data() as { _key?: string | number };
        return `${action}-${item?._key ?? ''}`;
    }

    getEditTooltip(): string {
        const config = this.config();
        if (config.editTooltip) return config.editTooltip;

        return this.isBundleType()
            ? 'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.EDIT-BUNDLE'
            : 'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.EDIT-DELIVERY-NOTE';
    }

    getDeleteTooltip(): string {
        const config = this.config();
        if (config.deleteTooltip) return config.deleteTooltip;

        return this.isBundleType()
            ? 'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.DELETE-FOLDER'
            : 'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.UNBIND-DELIVERY-NOTE';
    }

    getDeletePermanentTooltip(): string {
        const config = this.config();
        if (config.deletePermanentTooltip) return config.deletePermanentTooltip;

        return 'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.DELETE-DELIVERY-NOTE';
    }

    getDeleteIcon(): string {
        return this.isBundleType() ? 'delete' : 'cut';
    }

    getDeliveryNotesCount(): number {
        const item = this.data();

        // Check for numberOfDeliveryNotes property (for folders/bundles)
        if (hasNumberOfDeliveryNotes(item)) {
            return item.numberOfDeliveryNotes;
        }

        // Check for deliveryNoteKeys array (for tours)
        if (
            item &&
            'deliveryNoteKeys' in item &&
            Array.isArray(item.deliveryNoteKeys)
        ) {
            return item.deliveryNoteKeys.length;
        }

        return 0;
    }

    getDeliveryNotesTooltip(): string {
        if (this.shouldShowAddNotesAction()) {
            return (
                this.config().addNotesTooltip ||
                'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.ADD-DELIVERY-NOTE'
            );
        }
        return 'OUTGOING-GOODS.TOGGLER.DELIVERY-NOTES';
    }

    // Action handler methods
    async handleView() {
        const viewHandler = this.config().viewEndpoint;
        if (viewHandler) {
            // Pass parent row context for delivery notes
            if (this.isDeliveryNoteType() && this.parentRow()) {
                await viewHandler(this.data(), this.parentRow());
            } else {
                await viewHandler(this.data());
            }
        }
    }

    async handleDownload() {
        const downloadHandler = this.config().downloadEndpoint;
        if (downloadHandler) {
            // Pass parent row context for delivery notes
            if (this.isDeliveryNoteType() && this.parentRow()) {
                await downloadHandler(this.data(), this.parentRow());
            } else {
                await downloadHandler(this.data());
            }
        }
    }

    async handleEdit() {
        const editHandler = this.config().editEndpoint;
        if (editHandler) {
            // Pass parent row context for delivery notes
            if (this.isDeliveryNoteType() && this.parentRow()) {
                await editHandler(this.data(), this.parentRow());
            } else {
                await editHandler(this.data());
            }
        }
    }

    async handleDelete() {
        const deleteHandler = this.config().deleteEndpoint;
        if (deleteHandler) {
            if (this.isDeliveryNoteType()) {
                const bundleKey = this.config().bundleKey;
                if (this.parentRow()) {
                    await deleteHandler(
                        this.data(),
                        bundleKey,
                        this.parentRow()
                    );
                } else if (bundleKey) {
                    await deleteHandler(this.data(), bundleKey);
                } else {
                    await deleteHandler(this.data());
                }
            } else {
                // Bundle delete
                await deleteHandler(this.data());
            }
        }
    }

    async handleWalletInvite() {
        const walletInviteHandler = this.config().walletInviteEndpoint;
        if (walletInviteHandler) {
            await walletInviteHandler(this.data());
        }
    }

    async handleAddNotes() {
        if (this.shouldShowAddNotesAction()) {
            const addNotesHandler = this.config().addNotesEndpoint;
            if (addNotesHandler) {
                // Pass parent row context for delivery notes
                if (this.isDeliveryNoteType() && this.parentRow()) {
                    await addNotesHandler(this.data(), this.parentRow());
                } else {
                    await addNotesHandler(this.data());
                }
            }
        }
    }

    async handleViewPdf() {
        const viewPdfHandler = this.config().viewPdfEndpoint;
        if (viewPdfHandler) {
            await viewPdfHandler(this.data());
        }
    }

    async handleGoToProcess() {
        const goToProcessHandler = this.config().goToProcessEndpoint;
        if (goToProcessHandler) {
            await goToProcessHandler(this.data());
        }
    }

    async handleDeletePermanent() {
        const deletePermanentHandler = this.config().deletePermanentEndpoint;
        if (deletePermanentHandler) {
            await deletePermanentHandler(this.data());
        }
    }
}
